import markerSDK from '@marker.io/browser'

let markerWidget = null
let isInitialized = false
let initializationPromise = null

/**
 * Check if Marker.io should be available for the user.
 * Marker.io is allowed for any logged-in user.
 * @param {Object} userInfo - User object from Redux store (state.user)
 * @returns {boolean}
 */
export const isMarkerIOUserAllowed = (userInfo) => {
  return Boolean(userInfo?.uuid) && userInfo?.role !== "guest"
}

/**
 * Initialize Marker.io widget
 * @param {string} projectId - Marker.io project ID
 * @returns {Promise} - Promise that resolves when widget is loaded
 */
export const initializeMarkerIO = async (projectId) => {
  if (isInitialized && markerWidget) {
    return markerWidget
  }

  if (initializationPromise) {
    return initializationPromise
  }

  if (!projectId) {
    console.warn('Marker.io project ID is not provided')
    return null
  }

  initializationPromise = (async () => {
    try {
      markerWidget = await markerSDK.loadWidget({
        project: projectId,
      })
      isInitialized = true
      console.log('Marker.io widget initialized successfully')
      return markerWidget
    } catch (error) {
      console.error('Failed to initialize Marker.io widget:', error)
      isInitialized = false
      initializationPromise = null
      return null
    }
  })()

  return initializationPromise
}

/**
 * Open Marker.io feedback widget
 * @param {Object} userInfo - User object to check login state (optional)
 * @returns {Promise} - Promise that resolves when widget is opened
 */
export const openMarkerIO = async (userInfo = null) => {
  if (userInfo && !isMarkerIOUserAllowed(userInfo)) {
    console.warn("Marker.io: user must be logged in to report issues.")
    return
  }

  if (!markerWidget && !isInitialized) {
    const projectId = process.env.REACT_APP_MARKER_IO_PROJECT_ID
    if (projectId) {
      await initializeMarkerIO(projectId)
    } else {
      console.error('Marker.io is not initialized.')
      return
    }
  }

  if (markerWidget) {
    try {
      markerWidget.show()
      markerWidget.capture('fullscreen')
    } catch (error) {
      console.error('Failed to open Marker.io widget:', error)
    }
  } else {
    console.error('Marker.io widget is not available')
  }
}

/**
 * Hide Marker.io feedback widget
 */
export const hideMarkerIO = () => {
  if (markerWidget) {
    try {
      markerWidget.hide()
    } catch (error) {
      console.error('Failed to hide Marker.io widget:', error)
    }
  }
}

/**
 * Check if Marker.io is initialized
 * @returns {boolean}
 */
export const isMarkerIOInitialized = () => {
  return isInitialized && markerWidget !== null
}

/**
 * Get the Marker.io widget instance
 * @returns {Object|null} - Marker.io widget instance or null
 */
export const getMarkerIOWidget = () => {
  return markerWidget
}
